<style scoped>
/* The map is the page: every panel floats over it, so it takes the whole
   viewport and the panels own the gutters. */
.operations-map {
	position: absolute;
	inset: 0;
}

.operations-map :deep(.maplibregl-map) {
	width: 100%;
	height: 100%;
}

.operations-map :deep(.maplibregl-canvas) {
	width: 100%;
	height: 100%;
}

/* A technician's last known position: a circle whose border is the fix's age,
 * set inline from the ramp so the ring and the tooltip's stated age cannot
 * disagree. The avatar fills it; the white ground under the picture keeps the
 * ring legible against whatever the map has behind it. */
.tech-pin {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 34px;
	overflow: hidden;
	border: 3px solid;
	border-radius: 50%;
	background: #fff;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
	cursor: pointer;
}

.tech-pin__photo {
	display: block;
	width: 100%;
	height: 100%;
	border-radius: 50%;
	object-fit: cover;
}

/* The initials of a member with no avatar, at the footprint a photo takes, so
 * the pin keeps one size across the roster. */
.tech-pin__initials {
	color: #212529;
	font-size: 0.7rem;
	font-weight: 700;
	line-height: 1;
}

/* The selection halo is a shadow rather than a second border: the border is the
 * fix's age, and replacing it would hide how old the position is at exactly the
 * moment the operator is deciding whether to send someone there. */
.tech-pin--selected {
	box-shadow:
		0 0 0 3px rgba(13, 110, 253, 0.45),
		0 1px 4px rgba(0, 0, 0, 0.45);
}
</style>

<template>
	<div class="operations-map">
		<mgl-map
			:map-style="mapStyleURL(ALIDADE_SMOOTH)"
			:center="center"
			:zoom="5"
			@map:load="onMapLoad"
		>
			<!-- The district's parcels first, so they are the bottom of the stack:
			     every other layer is read against the ground it covers, and a
			     pick that lands between Sites names the parcel it landed on. -->
			<SourceParcel />

			<!-- The district boundary next: every line and point is read against
			     it. The satellite area is the map-review layer's own frame and
			     means nothing here. -->
			<SourceServiceArea
				layer-id="operations-service-area"
				:show-aoi-boundary="false"
				@service-area-loaded="onServiceAreaLoaded"
			/>

			<!-- Work Order footprints, from the same planning tiles the
			     Planning workbench draws: the geography is one set of H3 cells
			     per open Work Order, so the two surfaces cannot disagree about
			     where the work is.

			     Only the open footprints are drawn (`workOrderFilter` below): a
			     converted or cancelled Work Order is history, and the Operations
			     map is the office's live ground. The match arms still name every
			     state, so the encoding is stated where the colour is chosen
			     rather than implied by what happens to be drawn. -->
			<SourceTegola id="operations-work-orders" map-name="planning">
				<mgl-fill-layer
					layer-id="operations-work-order-fill"
					source-layer="planning-work-order-cell"
					:filter="workOrderFilter as any"
					:paint="workOrderFillPaint as any"
				/>
				<mgl-line-layer
					layer-id="operations-work-order-outline"
					source-layer="planning-work-order-cell"
					:filter="workOrderFilter as any"
					:paint="workOrderLinePaint as any"
				/>
				<!-- The checked footprints, outlined above the wash: the ledger's
				     checkboxes and the map's outlines are the same set. -->
				<mgl-line-layer
					layer-id="operations-work-order-selected"
					source-layer="planning-work-order-cell"
					:filter="selectedWorkOrderFilter as any"
					:paint="workOrderSelectedPaint as any"
				/>
			</SourceTegola>

			<!-- Route lines: one LineString per Route, through its sequenced
			     members' own targets. The colour is the route's palette slot and
			     the width/opacity carry the selection emphasis, so a selection
			     reads on the map without a second layer. -->
			<mgl-geo-json-source
				source-id="operations-route-lines"
				:data="routeLines"
			>
				<!-- `line-cap` and `line-join` are layout properties, not paint ones:
				     a paint object carrying them makes MapLibre refuse the whole
				     layer at addLayer time ("unknown property"), which is why they
				     are stated here and the paint object below holds none. -->
				<mgl-line-layer
					layer-id="operations-routes"
					:layout="{ 'line-cap': 'round', 'line-join': 'round' }"
					:paint="routePaint as any"
				/>
			</mgl-geo-json-source>

			<!-- The Sites as the small neutral dot a pick selects. The work an
			     Assignment names is placed at a Site, so the map shows the places
			     it can be placed at; the dot sits above the route lines (a Site
			     that cannot be seen cannot be picked) and below the stations'
			     rings, whose centre belongs to the instance drawn there. -->
			<mgl-geo-json-source source-id="operations-sites" :data="siteDots">
				<mgl-circle-layer
					layer-id="operations-sites"
					:paint="{
						'circle-color': '#343a40',
						'circle-radius': 4,
						'circle-stroke-color': '#ffffff',
						'circle-stroke-width': 1,
					}"
				/>
			</mgl-geo-json-source>

			<!-- The standing stations, as the ring around the trap instances set
			     at them. The ring is the station's only affordance, so it sits
			     above the route lines; but it sits below the stops and the
			     instances, and the declared hit-test policy puts the station
			     after both of them, so a click on the point inside a ring
			     reaches the instance drawn there rather than the station
			     around it. -->
			<mgl-geo-json-source
				source-id="operations-stations"
				:data="stationPoints"
			>
				<mgl-circle-layer
					layer-id="operations-stations"
					:paint="stationPaint"
				/>
			</mgl-geo-json-source>

			<!-- Members with no sequence yet, drawn as stops rather than as line
			     vertices: an unsequenced member has a place but no place in the
			     order, and a one-stop Route has no line at all. -->
			<mgl-geo-json-source
				source-id="operations-route-markers"
				:data="routeMarkerPoints"
			>
				<mgl-circle-layer
					layer-id="operations-route-markers"
					:paint="markerPaint"
				/>
			</mgl-geo-json-source>

			<!-- The trap instances: orange while the trap is out (awaiting
			     collection), grey once collected. Clicking one selects it. -->
			<mgl-geo-json-source source-id="operations-traps" :data="trapPoints">
				<mgl-circle-layer layer-id="operations-traps" :paint="trapPaint" />
			</mgl-geo-json-source>

			<!-- The selected Assignment's target, which is not otherwise drawn:
			     the work is a place the map should point at while its details are
			     open. -->
			<mgl-geo-json-source
				source-id="operations-selected-target"
				:data="selectedTarget"
			>
				<mgl-circle-layer
					layer-id="operations-selected-target"
					:paint="selectedTargetPaint"
				/>
			</mgl-geo-json-source>

			<!-- What the authoring form is collecting, drawn above everything:
			     the target is the thing being read while the form is open. A
			     point-like target is a ring at its point; an area is its own
			     outline, so the operator sees the shape they drew rather than
			     the centre of its box. -->
			<mgl-geo-json-source
				source-id="operations-pick-targets"
				:data="pickTargetPoints"
			>
				<mgl-circle-layer
					layer-id="operations-pick-targets"
					:paint="{
						'circle-color': '#0d6efd',
						'circle-radius': 9,
						'circle-opacity': 0.22,
						'circle-stroke-color': '#0d6efd',
						'circle-stroke-width': 2,
						'circle-stroke-opacity': 0.9,
					}"
				/>
			</mgl-geo-json-source>

			<mgl-geo-json-source
				source-id="operations-pick-areas"
				:data="pickTargetAreas"
			>
				<mgl-fill-layer
					layer-id="operations-pick-areas"
					:paint="{ 'fill-color': '#0d6efd', 'fill-opacity': 0.18 }"
				/>
				<mgl-line-layer
					layer-id="operations-pick-areas-outline"
					:paint="{ 'line-color': '#0d6efd', 'line-width': 2 }"
				/>
			</mgl-geo-json-source>

			<!-- The ring the operator drew comes back in as `area`, so an area
			     selection stays visible after the widget has dropped its own copy. -->
			<AreaDraw
				source-id="operations-area-draw"
				:modes="['rectangle', 'polygon']"
				:mode="drawMode"
				:committed="committedAreas"
				@commit="onAreaCommit"
				@cancel="emit('area-cancel')"
			/>

			<!-- Click resolution lives here, so an authoring pick and a selection
			     never race: while the form is collecting a target, or an area tool
			     is armed, that gesture owns every click, and the widget answers
			     none of them.

			     The armed-area check is not restricted to the area pick: terra-draw's
			     rectangle and polygon modes CLOSE on a click, and that click is
			     delivered here too. If this answered it, it would deselect — or
			     clear — the selection the draw has just made, and which of the two
			     runs first is only listener registration order. Disabling for the
			     whole gesture makes that order irrelevant. -->
			<FeatureSelect
				:layers="selectLayers"
				:disabled="props.pickMode !== null || props.areaMode !== null"
				@select="onFeatureSelect"
				@clear="onFeatureClear"
			/>

			<!-- The technicians' last known fixes, as avatars over everything: a
			     marker is a DOM element rather than a WebGL layer, so it sits above
			     the whole stack without a layer of its own. The ring is the fix's
			     age (green now, amber an hour, grey two hours on) and the avatar
			     fills it, so every pin is the same size however old its position
			     is. Clicking one selects that technician, as their ledger row
			     does. -->
			<mgl-marker
				v-for="position in placedTechnicians"
				:key="position.user_id"
				:coordinates="[position.longitude, position.latitude]"
				anchor="center"
			>
				<template #marker>
					<span
						class="tech-pin"
						:class="{
							'tech-pin--selected': selectedTechnicianId === position.user_id,
						}"
						:style="{
							borderColor: recencyColor(now - Date.parse(position.fix_time)),
						}"
						:title="`${position.display_name} — fix ${fixAgeText(position.fix_time)}`"
						@click.stop="onTechnicianClick(position.user_id, $event)"
						@mousedown.stop
					>
						<img
							v-if="position.avatar"
							:src="position.avatar"
							:alt="position.display_name"
							class="tech-pin__photo"
						/>
						<span v-else class="tech-pin__initials">
							{{ position.initials }}
						</span>
					</span>
				</template>
			</mgl-marker>
		</mgl-map>
	</div>
</template>

<script setup lang="ts">
import "maplibre-gl/dist/maplibre-gl.css";

import {
	computed,
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref,
	shallowRef,
	watch,
} from "vue";
import {
	MglCircleLayer,
	MglFillLayer,
	MglGeoJsonSource,
	MglLineLayer,
	MglMap,
	MglMarker,
	type MglEvent,
} from "vue-maplibre-gl";
import maplibregl from "maplibre-gl";
import type {
	CircleLayerSpecification,
	FilterSpecification,
	LineLayerSpecification,
	Map as MapLibreMap,
	MapMouseEvent,
} from "maplibre-gl";
import type { GeoJSON } from "geojson";

import SourceParcel from "@/map/SourceParcel.vue";
import SourceServiceArea from "@/map/SourceServiceArea.vue";
import SourceTegola from "@/map/SourceTegola.vue";
import { ALIDADE_SMOOTH } from "@/map/constants";
import { boundsDefault, boundsFromGeoJSON, mapStyleURL } from "@/map/util";
import { parcelHitFromFeatures } from "@/lib/stationMap";
import { cellBoundaryRing } from "@/lib/h3/cells";
import {
	AreaDraw,
	FeatureSelect,
	featuresInRing,
	type AreaCommit,
	type AreaMode,
	type MapSelectPayload,
	type SelectId,
	type SelectLayer,
} from "@/map/select";
import type {
	MapReviewSiteRow,
	MapReviewTrapInstanceRow,
	MapReviewTrapStationRow,
	OperationsTechnicianPosition,
	SyncAssignment,
	SyncRoute,
} from "@/type/api";

import {
	assignmentPoint,
	geometryPoint,
	pickTargetPoint,
	routeColor,
	routeLine,
	routeMarkers,
	routeStops,
	sitePoint,
	stationPoint,
	TRAP_COLLECTED_COLOR,
	TRAP_OPEN_COLOR,
	type MapPickTarget,
	type RouteStop,
	type Selection,
	type TargetDrawMode,
} from "./model";
import { recencyColor } from "./recency";

const props = withDefaults(
	defineProps<{
		assignments: SyncAssignment[];
		routes: SyncRoute[];
		stations: MapReviewTrapStationRow[];
		traps: MapReviewTrapInstanceRow[];
		sites: MapReviewSiteRow[];
		/** The store record, narrowed to a numeric row id. */
		selection: Selection | null;
		/** The store's multi-selection: the work-order highlight reads the
		 * `work_order:` keys out of it. */
		selectedKeys: Set<string>;
		/** The selected work order's cells, for camera framing; empty when the
		 * footprint is not known. */
		selectedWorkOrderCells: string[];
		/** What the map is collecting for an authoring form; absent while the
		 * page is only reading the collection. */
		pickMode?: TargetDrawMode | null;
		/** The targets the form already holds, drawn as the picks in force. */
		targets?: MapPickTarget[];
		/** The armed area-selection tool; null when not drawing. */
		areaMode?: AreaMode | null;
		/** The selection kind the active ledger tab names, or null when it has
		 * no map layer to hit. */
		areaKind?: string | null;
		/** The committed selection ring, redrawn by `AreaDraw`. */
		area?: GeoJSON.Polygon | null;
		/** Each technician's last known fix, as the overlay's pins: one row per
		 * member whose device reported a valid position. */
		positions: OperationsTechnicianPosition[];
		/** Whether the overlay is drawn at all; the pane's checkbox owns it. */
		showTechnicians: boolean;
	}>(),
	{
		pickMode: null,
		targets: () => [],
		areaMode: null,
		areaKind: null,
		area: null,
	},
);

const emit = defineEmits<{
	select: [payload: MapSelectPayload];
	clear: [];
	pick: [target: MapPickTarget];
	drawn: [geometry: GeoJSON.Point | GeoJSON.Polygon];
	area: [{ kind: string; ids: SelectId[]; ring: [number, number][] }];
	"area-cancel": [];
	/** A technician's pin clicked: selecting them, additively when the gesture
	 * carried a modifier. The page turns this into the same store select a
	 * ledger row makes. */
	"select-technician": [{ id: number; additive: boolean }];
}>();

/** The map's opening centre: the continental-US default, replaced by the
 * district fit as soon as the map and its boundary are both ready. */
const initialCenter = boundsDefault().getCenter();
const center: [number, number] = [initialCenter.lng, initialCenter.lat];

const mapRef = shallowRef<MapLibreMap | null>(null);
const serviceArea = ref<GeoJSON.GeoJSON | null>(null);

const assignmentIndex = computed(
	() => new Map(props.assignments.map((row) => [row.id, row])),
);
const siteIndex = computed(
	() => new Map(props.sites.map((row) => [row.id, row])),
);

/** Every Site the district has a location for, as the dot that makes it
 * pickable. A Site with no recorded point cannot be picked off the map, so it
 * has no dot. */
const siteDots = computed<
	GeoJSON.FeatureCollection<GeoJSON.Point, SiteDotProperties>
>(() => {
	const features: GeoJSON.Feature<GeoJSON.Point, SiteDotProperties>[] = [];
	for (const site of props.sites) {
		const point = sitePoint(site);
		if (point === null) continue;
		features.push({
			type: "Feature",
			geometry: { type: "Point", coordinates: point },
			properties: { id: site.id },
		});
	}
	return { type: "FeatureCollection", features };
});

/** The Route whose line the current selection emphasises: the selected Route
 * itself, or the Route the selected Assignment belongs to. */
const selectedRouteId = computed<number | null>(() => {
	if (props.selection === null) return null;
	if (props.selection.kind === "route") return props.selection.id;
	if (props.selection.kind === "assignment") {
		return (
			props.assignments.find((row) => row.id === props.selection?.id)
				?.routeId ?? null
		);
	}
	return null;
});

const selectedTechnicianId = computed<number | null>(() =>
	props.selection?.kind === "technician" ? props.selection.id : null,
);

/** The pins the map draws: only while the overlay is on, and only for a row the
 * map can place. A latitude or longitude that is not a finite number cannot be
 * given to MapLibre at all, so such a row is left off rather than pinned at the
 * Gulf of Guinea. */
const placedTechnicians = computed<OperationsTechnicianPosition[]>(() =>
	props.showTechnicians
		? props.positions.filter(
				(position) =>
					Number.isFinite(position.latitude) &&
					Number.isFinite(position.longitude),
			)
		: [],
);

/** The clock a pin's ring is measured against. Advancing it is the only thing
 * the overlay's timer does: a fix ages while no new one is arriving, and nothing
 * here asks the server for anything — positions arrive on the store's own SSE
 * event. */
const now = ref(Date.now());
let rampTimer: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
	rampTimer = setInterval(() => {
		now.value = Date.now();
	}, 30_000);
});

onBeforeUnmount(() => {
	if (rampTimer !== null) {
		clearInterval(rampTimer);
		rampTimer = null;
	}
});

/** How old a fix is, in the words the pin's tooltip uses: the ring states the age
 * as a colour, so the age itself has to be readable somewhere. */
function fixAgeText(fixTime: string): string {
	const ageMs = now.value - Date.parse(fixTime);
	// An unreadable timestamp has no age to state, which is also why its ring is
	// grey rather than one of the ramp's stops.
	if (Number.isNaN(ageMs)) return "time unknown";
	if (ageMs < 60_000) return "just now";
	const minutes = Math.floor(ageMs / 60_000);
	if (minutes < 60) return `${minutes} min ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours} h ago`;
	return `${Math.floor(hours / 24)} d ago`;
}

/** A pin clicked: the same act as clicking that technician's ledger row, with the
 * gesture's modifiers making it additive. While an authoring form is collecting a
 * target, or an area tool is armed, the map's gestures belong to that tool — the
 * form owns its own picks, so the pin answers nothing. */
function onTechnicianClick(id: number, event: MouseEvent) {
	if (props.pickMode !== null || props.areaMode !== null) return;
	emit("select-technician", {
		id,
		additive: event.ctrlKey || event.metaKey || event.shiftKey,
	});
}

/** The width and opacity a route draws with: the selection is a reading of the
 * whole map, so an unselected route dims rather than disappearing. */
function routeEmphasis(route: SyncRoute): { width: number; opacity: number } {
	if (props.selection === null) return { width: 4, opacity: 0.9 };
	if (selectedRouteId.value === route.id) return { width: 6, opacity: 1 };
	if (
		selectedTechnicianId.value !== null &&
		route.technicianId === selectedTechnicianId.value
	) {
		return { width: 5, opacity: 1 };
	}
	return { width: 3, opacity: 0.22 };
}

interface RouteLineProperties {
	id: number;
	color: string;
	width: number;
	opacity: number;
}

interface RouteMarkerProperties {
	id: number;
	color: string;
	opacity: number;
	radius: number;
}

interface TrapProperties {
	id: number;
	color: string;
	radius: number;
	stroke: string;
	strokeWidth: number;
}

interface StationProperties {
	id: number;
	radius: number;
	strokeWidth: number;
	strokeOpacity: number;
}

interface SiteDotProperties {
	id: number;
}

const routeLines = computed<
	GeoJSON.FeatureCollection<GeoJSON.LineString, RouteLineProperties>
>(() => {
	const features: GeoJSON.Feature<GeoJSON.LineString, RouteLineProperties>[] =
		[];
	for (const route of props.routes) {
		const coordinates = routeLine(
			route,
			assignmentIndex.value,
			siteIndex.value,
		);
		if (coordinates.length < 2) continue;
		const emphasis = routeEmphasis(route);
		features.push({
			type: "Feature",
			geometry: { type: "LineString", coordinates },
			properties: {
				id: route.id,
				color: routeColor(route.id),
				width: emphasis.width,
				opacity: emphasis.opacity,
			},
		});
	}
	return { type: "FeatureCollection", features };
});

const routeMarkerPoints = computed<
	GeoJSON.FeatureCollection<GeoJSON.Point, RouteMarkerProperties>
>(() => {
	const features: GeoJSON.Feature<GeoJSON.Point, RouteMarkerProperties>[] = [];
	for (const route of props.routes) {
		const emphasis = routeEmphasis(route);
		const markers: RouteStop[] = routeMarkers(
			route,
			assignmentIndex.value,
			siteIndex.value,
		);
		for (const marker of markers) {
			features.push({
				type: "Feature",
				geometry: { type: "Point", coordinates: marker.point },
				properties: {
					id: marker.assignmentId,
					color: routeColor(route.id),
					opacity: emphasis.opacity,
					radius: marker.position === null ? 4.5 : 5.5,
				},
			});
		}
	}
	return { type: "FeatureCollection", features };
});

const trapPoints = computed<
	GeoJSON.FeatureCollection<GeoJSON.Point, TrapProperties>
>(() => {
	const features: GeoJSON.Feature<GeoJSON.Point, TrapProperties>[] = [];
	for (const trap of props.traps) {
		if (trap.latitude === null || trap.longitude === null) continue;
		const selected =
			props.selection?.kind === "trap" && props.selection.id === trap.id;
		features.push({
			type: "Feature",
			geometry: { type: "Point", coordinates: [trap.longitude, trap.latitude] },
			properties: {
				id: trap.id,
				color:
					trap.collected_at === null ? TRAP_OPEN_COLOR : TRAP_COLLECTED_COLOR,
				radius: selected ? 9 : 6,
				stroke: selected ? "#212529" : "#ffffff",
				strokeWidth: selected ? 3 : 1.5,
			},
		});
	}
	return { type: "FeatureCollection", features };
});

/** The stations' rings. A station that is not the selection dims while some
 * other thing is open — not hidden, because the network is still the context
 * the selection is read against. */
const stationPoints = computed<
	GeoJSON.FeatureCollection<GeoJSON.Point, StationProperties>
>(() => {
	const features: GeoJSON.Feature<GeoJSON.Point, StationProperties>[] = [];
	for (const station of props.stations) {
		const point = stationPoint(station);
		if (point === null) continue;
		const selected =
			props.selection?.kind === "station" && props.selection.id === station.id;
		features.push({
			type: "Feature",
			geometry: { type: "Point", coordinates: point },
			properties: {
				id: station.id,
				radius: selected ? 12 : 9,
				strokeWidth: selected ? 2.5 : 1.5,
				strokeOpacity: selected || props.selection === null ? 1 : 0.3,
			},
		});
	}
	return { type: "FeatureCollection", features };
});

const selectedTarget = computed<GeoJSON.FeatureCollection<GeoJSON.Point>>(
	() => {
		const features: GeoJSON.Feature<GeoJSON.Point>[] = [];
		if (props.selection?.kind === "assignment") {
			const assignment = assignmentIndex.value.get(props.selection.id);
			if (assignment) {
				const point = assignmentPoint(assignment, siteIndex.value);
				if (point) {
					features.push({
						type: "Feature",
						geometry: { type: "Point", coordinates: point },
						properties: {},
					});
				}
			}
		}
		return { type: "FeatureCollection", features };
	},
);

/** The point-like targets the form already holds, at the point each stands at:
 * a Site through the index, a parcel where the pick landed, a drawn point at
 * itself. An area is drawn as its own shape rather than as its centre. */
const pickTargetPoints = computed<GeoJSON.FeatureCollection<GeoJSON.Point>>(
	() => {
		const features: GeoJSON.Feature<GeoJSON.Point>[] = [];
		for (const target of props.targets) {
			if (target.kind === "area") continue;
			const point = pickTargetPoint(target, siteIndex.value);
			if (point === null) continue;
			features.push({
				type: "Feature",
				geometry: { type: "Point", coordinates: point },
				properties: {},
			});
		}
		return { type: "FeatureCollection", features };
	},
);

/** The area targets the form holds, as the polygons the map draws them from. */
const pickTargetAreas = computed<GeoJSON.FeatureCollection<GeoJSON.Polygon>>(
	() => {
		const features: GeoJSON.Feature<GeoJSON.Polygon>[] = [];
		for (const target of props.targets) {
			if (target.kind === "area") {
				features.push({
					type: "Feature",
					geometry: target.geometry,
					properties: {},
				});
			}
		}
		return { type: "FeatureCollection", features };
	},
);

/* The drawn set: the open footprints. A Work Order's H3 cells are drawn by the
   planning tiles, and the Operations map is the office's live ground — a
   converted or cancelled footprint is history and belongs to the Work Order, not
   to the map a dispatcher reads. The paint objects below still spell all three
   states, so a surface that widens the filter inherits the encoding rather than
   inventing one: orange is live work, grey is settled, pale grey is withdrawn. */
const workOrderFilter = ["==", ["get", "state"], "open"] as FilterSpecification;

/** The ids the store's multi-selection names as work orders. The key is the
 * store's `${kind}:${id}`, so the kind is read back off it rather than assumed:
 * the set also holds every checked assignment, route, trap and station. */
const selectedWorkOrderIds = computed(() =>
	[...props.selectedKeys]
		.filter((key) => key.startsWith("work_order:"))
		.map((key) => Number(key.slice("work_order:".length)))
		.filter((id) => Number.isFinite(id)),
);

/** The highlight outlines, as a filter over the same tiles. An empty selection
 * asks for an id no row has, which draws nothing without a second layer. */
const selectedWorkOrderFilter = computed<FilterSpecification>(() =>
	selectedWorkOrderIds.value.length === 0
		? ["==", ["get", "id"], -1]
		: ["in", ["get", "id"], ["literal", selectedWorkOrderIds.value]],
);

const workOrderFillPaint = {
	"fill-color": [
		"match",
		["get", "state"],
		"open",
		"#fd7e14",
		"converted",
		"#6c757d",
		"cancelled",
		"#adb5bd",
		"#adb5bd",
	],
	"fill-opacity": [
		"match",
		["get", "state"],
		"open",
		0.16,
		"converted",
		0.1,
		0.08,
	],
};

const workOrderLinePaint = {
	"line-color": [
		"match",
		["get", "state"],
		"open",
		"#b35c00",
		"converted",
		"#495057",
		"cancelled",
		"#868e96",
		"#868e96",
	],
	"line-width": 1,
	"line-dasharray": [2, 2],
	"line-opacity": 0.7,
};

const workOrderSelectedPaint = {
	"line-color": "#0d6efd",
	"line-width": 2.5,
	"line-opacity": 1,
};

/** The middle vertex of a line: a Route's line has no single point of its own,
 * and one representative point per feature is what `featuresInRing` tests, so
 * the vertex nearest the middle of the sequence is where the line is "at". */
function lineMidpoint(feature: GeoJSON.Feature): [number, number] | null {
	const geometry = feature.geometry;
	const line =
		geometry?.type === "LineString"
			? geometry.coordinates
			: geometry?.type === "MultiLineString"
				? geometry.coordinates[0]
				: null;
	if (!line || line.length === 0) return null;
	const middle = line[Math.floor(line.length / 2)];
	return middle ? [middle[0], middle[1]] : null;
}

/**
 * The map's hit-testing policy, declared once. `FeatureSelect` tests clicks and
 * `featuresInRing` tests drawn areas against this same list, so a click and a
 * sweep cannot disagree about what a trap, a station or a Work Order is.
 *
 * The priorities are the order a click is read in: a trap instance, the stop
 * drawn on it, the station's ring around both, the route line, and last the
 * Work Order wash beneath everything — a footprint is the ground the other
 * objects stand on, so it only answers where nothing else did. A point layer is
 * queried with a small tolerance box because its dot is a few pixels wide; a
 * fill is what the operator aimed at, so the click itself is the query.
 */
const selectLayers = computed<SelectLayer[]>(() => [
	{ layerId: "operations-traps", kind: "trap", priority: 0, target: "point" },
	{
		layerId: "operations-route-markers",
		kind: "assignment",
		priority: 1,
		target: "point",
	},
	{
		layerId: "operations-stations",
		kind: "station",
		priority: 2,
		target: "point",
	},
	{
		layerId: "operations-routes",
		kind: "route",
		priority: 3,
		pointOf: lineMidpoint,
	},
	{
		layerId: "operations-work-order-fill",
		kind: "work_order",
		priority: 4,
		target: "point",
		pointOf: (feature) => geometryPoint(feature.geometry),
	},
]);

/** The armed draw tool. An authoring form's area pick is the clicked-vertex ring
 * — a district area is a handful of corners, not a drag — while the Map tools
 * rail arms whichever mode the operator chose. */
const drawMode = computed<AreaMode | null>(() =>
	props.pickMode === "area" ? "polygon" : props.areaMode,
);

/** The ring handed back to the widget to redraw. An authoring area is drawn by
 * the targets' own layer below, so only a committed selection is redrawn here;
 * drawing the form's ring twice would read as two shapes. */
const committedAreas = computed(() =>
	props.pickMode === "area" || !props.area ? [] : [props.area],
);

// MapLibre's paint typing cannot be assembled from a data-driven expression
// without a cast: the expression array is a valid spec, but the property's type
// union is not inferred from a plain array literal.
//
// `line-cap` and `line-join` are layout properties, not paint ones: a paint
// object carrying them makes MapLibre refuse the whole layer at addLayer time
// ("unknown property"), so the layer states them in its `layout` prop.
const routePaint = {
	"line-color": ["get", "color"],
	"line-width": ["get", "width"],
	"line-opacity": ["get", "opacity"],
} as unknown as LineLayerSpecification["paint"];

const markerPaint = {
	"circle-color": ["get", "color"],
	"circle-radius": ["get", "radius"],
	"circle-opacity": ["get", "opacity"],
	"circle-stroke-color": "#ffffff",
	"circle-stroke-width": 1.5,
} as unknown as CircleLayerSpecification["paint"];

const trapPaint = {
	"circle-color": ["get", "color"],
	"circle-radius": ["get", "radius"],
	"circle-stroke-color": ["get", "stroke"],
	"circle-stroke-width": ["get", "strokeWidth"],
} as unknown as CircleLayerSpecification["paint"];

// The fill is left fully transparent so the instance drawn at the station's
// point is not tinted by the ring around it; the stroke alone carries the
// station. MapLibre still hit-tests the full circle, so the inside of the ring
// stays the instance's and only the band between the two radii is the ring's.
const stationPaint = {
	"circle-color": "#495057",
	"circle-opacity": 0,
	"circle-radius": ["get", "radius"],
	"circle-stroke-color": "#495057",
	"circle-stroke-width": ["get", "strokeWidth"],
	"circle-stroke-opacity": ["get", "strokeOpacity"],
} as unknown as CircleLayerSpecification["paint"];

const selectedTargetPaint = {
	"circle-color": "#0d6efd",
	"circle-radius": 16,
	"circle-opacity": 0.18,
	"circle-stroke-color": "#0d6efd",
	"circle-stroke-width": 2,
	"circle-stroke-opacity": 0.85,
} as unknown as CircleLayerSpecification["paint"];

/** The camera's frame for a set of points. The bottom padding clears the
 * details sheet, which floats over the map rather than beside it. */
function fitPoints(points: [number, number][]) {
	const map = mapRef.value;
	if (!map || points.length === 0) return;
	const bounds = new maplibregl.LngLatBounds();
	for (const point of points) bounds.extend(point);
	const height = map.getContainer().clientHeight;
	map.fitBounds(bounds, {
		padding: {
			top: 80,
			right: 80,
			bottom: Math.round(Math.min(height * 0.45, 340)),
			left: 80,
		},
		maxZoom: 15,
		duration: 700,
	});
}

function fitDistrict() {
	const map = mapRef.value;
	if (!map) return;
	const bounds = serviceArea.value
		? boundsFromGeoJSON(serviceArea.value)
		: null;
	if (bounds && !bounds.isEmpty()) {
		map.fitBounds(bounds, { padding: 60, duration: 700 });
		return;
	}
	map.fitBounds(boundsDefault(), { padding: 60, duration: 700 });
}

/** Frame what is selected: the route's own stops, the technician's routes, a
 * trap's or station's point, and the district when nothing is. */
function fitSelection() {
	const map = mapRef.value;
	if (!map) return;
	const selection = props.selection;
	if (selection === null) {
		fitDistrict();
		return;
	}
	if (selection.kind === "route") {
		const route = props.routes.find(
			(candidate) => candidate.id === selection.id,
		);
		if (route) {
			const points = routeStops(
				route,
				assignmentIndex.value,
				siteIndex.value,
			).map((stop) => stop.point);
			if (points.length > 0) {
				fitPoints(points);
				return;
			}
		}
	}
	if (selection.kind === "technician") {
		const points = props.routes
			.filter((route) => route.technicianId === selection.id)
			.flatMap((route) =>
				routeStops(route, assignmentIndex.value, siteIndex.value).map(
					(stop) => stop.point,
				),
			);
		if (points.length > 0) {
			fitPoints(points);
			return;
		}
		// A technician with no route has no line to frame, but they do have a
		// place: their last known fix is what the selection is about, so the
		// camera goes there rather than pulling back to the whole district. The
		// zoom floor keeps a single point from filling the pane with one block.
		const position = props.positions.find(
			(row) => row.user_id === selection.id,
		);
		if (
			position &&
			Number.isFinite(position.latitude) &&
			Number.isFinite(position.longitude)
		) {
			map.easeTo({
				center: [position.longitude, position.latitude],
				zoom: Math.max(map.getZoom(), 13),
				duration: 500,
			});
			return;
		}
	}
	if (selection.kind === "trap") {
		const trap = props.traps.find((candidate) => candidate.id === selection.id);
		if (trap && trap.latitude !== null && trap.longitude !== null) {
			map.easeTo({
				center: [trap.longitude, trap.latitude],
				zoom: Math.max(map.getZoom(), 14),
				duration: 500,
			});
			return;
		}
	}
	if (selection.kind === "station") {
		const station = props.stations.find(
			(candidate) => candidate.id === selection.id,
		);
		const point = station ? stationPoint(station) : null;
		if (point) {
			map.easeTo({
				center: point,
				zoom: Math.max(map.getZoom(), 14),
				duration: 500,
			});
			return;
		}
	}
	if (selection.kind === "assignment") {
		const assignment = assignmentIndex.value.get(selection.id);
		const point = assignment
			? assignmentPoint(assignment, siteIndex.value)
			: null;
		if (point) {
			map.easeTo({
				center: point,
				zoom: Math.max(map.getZoom(), 14),
				duration: 500,
			});
			return;
		}
	}
	// A Work Order is a cell set, so it has no single point to centre on: the
	// camera fits the footprint's own bounds. The cells arrive with the page's
	// on-demand read, and until they do there is nothing to frame — the camera
	// stays where the operator left it rather than pulling back to the district
	// for a selection that is still being resolved. A footprint with no cells of
	// its own has no place to go to either.
	if (selection.kind === "work_order") {
		const cells = props.selectedWorkOrderCells;
		if (cells.length === 0) return;
		let west = Infinity;
		let south = Infinity;
		let east = -Infinity;
		let north = -Infinity;
		for (const cell of cells) {
			for (const [lng, lat] of cellBoundaryRing(cell)) {
				if (lng < west) west = lng;
				if (lng > east) east = lng;
				if (lat < south) south = lat;
				if (lat > north) north = lat;
			}
		}
		// The zoom ceiling matches the Planning map's own footprint framing: one
		// cell filling the pane would lose the streets around it.
		if (Number.isFinite(west) && Number.isFinite(north)) {
			map.fitBounds(
				[
					[west, south],
					[east, north],
				],
				{ padding: 60, maxZoom: 16, duration: 500 },
			);
		}
		return;
	}
	fitDistrict();
}

function onServiceAreaLoaded(geometry: GeoJSON.GeoJSON | null) {
	serviceArea.value = geometry;
	if (props.selection === null) fitDistrict();
}

/** The target a pick names, in the order a pick means them: the Site dot under
 * the pointer, the Site the station's ring stands on, then the parcel the point
 * falls in. A station is read as its Site because a Site is what an Assignment
 * targets — the station only says where the work belongs — and a station the
 * props do not carry names nothing. The parcel comes from the shared parcel
 * reader, so a pick and the station map's own clicks agree on what a parcel is. */
function pickedTarget(
	map: MapLibreMap,
	event: MapMouseEvent,
): MapPickTarget | null {
	if (map.getLayer("operations-sites")) {
		const [hit] = map.queryRenderedFeatures(event.point, {
			layers: ["operations-sites"],
		});
		const siteId = Number(hit?.properties?.id);
		if (Number.isInteger(siteId)) return { kind: "site", siteId };
	}
	if (map.getLayer("operations-stations")) {
		const hits = map.queryRenderedFeatures(event.point, {
			layers: ["operations-stations"],
		});
		for (const hit of hits) {
			const stationId = Number(hit.properties?.id);
			const station = props.stations.find((row) => row.id === stationId);
			if (station) return { kind: "site", siteId: station.site_id };
		}
	}
	if (map.getLayer("parcel-fill")) {
		const parcel = parcelHitFromFeatures(
			map.queryRenderedFeatures(event.point, { layers: ["parcel-fill"] }),
		);
		if (parcel) {
			return {
				kind: "parcel",
				externalId: parcel.external_id,
				region: parcel.region,
				lng: event.lngLat.lng,
				lat: event.lngLat.lat,
			};
		}
	}
	return null;
}

/**
 * True from the moment a draw commits until the operator next presses the map.
 *
 * terra-draw completes a rectangle on `mouseup`, and the browser dispatches the
 * `click` for that press as a SEPARATE task afterwards — by the time it arrives
 * the tool is already disarmed and this widget is live again. That click hits
 * empty map, emits `clear`, and wipes the selection the draw has just made; it
 * is the same press, so nothing should be answered from it.
 *
 * The flag is cleared by the next `mousedown`, not by a timer: every real click
 * begins with a press, so the first genuine interaction re-arms selection, and a
 * draw whose closing click never arrives (a keyboard-finished polygon) cannot
 * swallow anything.
 */
let swallowClosingClick = false;

/** A click while the map is collecting for a form names a target instead of a
 * selection: a point is dropped where the pointer is and a pick reads the
 * feature under it. Collecting a target must never change what the panes and the
 * details sheet are reading; reading a click is `FeatureSelect`'s job, and the
 * area draw takes its own clicks. */
function onMapClick(event: MapMouseEvent) {
	const map = mapRef.value;
	if (!map) return;
	if (props.pickMode === "point") {
		emit("drawn", {
			type: "Point",
			coordinates: [event.lngLat.lng, event.lngLat.lat],
		});
		return;
	}
	if (props.pickMode === "pick") {
		const target = pickedTarget(map, event);
		// A pick that hits nothing says nothing: the pick is filling a form, so
		// an empty click must not disturb the selection being read.
		if (target !== null) emit("pick", target);
	}
}

/** A click resolved by the shared widget against the declared layers: one
 * payload naming what the pointer landed on and whether the gesture was
 * additive. */
function onFeatureSelect(payload: MapSelectPayload) {
	if (swallowClosingClick) return;
	emit("select", payload);
}

/** An empty-map click means "deselect" — the same answer a ledger gives to
 * clicking blank space. */
function onFeatureClear() {
	if (swallowClosingClick) return;
	emit("clear");
}

/** A drawn shape, in one of two meanings that depend on the mode.
 *
 * An authoring form collecting its area: the ring is the target the form holds.
 * Selecting: the ring names the objects of the active ledger tab inside it, read
 * through the same declared layers a click is, so the two gestures cannot
 * disagree about what counts as being in the area.
 */
function onAreaCommit({ ring }: AreaCommit) {
	const map = mapRef.value;
	if (!map) return;
	// The press that closed the draw also produces a `click` a task later, and
	// the selection that click would clear is the one this commit is about to
	// make.
	swallowClosingClick = true;
	if (props.pickMode === "area") {
		emit("drawn", { type: "Polygon", coordinates: [ring] });
		return;
	}
	const kind = props.areaKind;
	if (!kind) return;
	const ids =
		featuresInRing(
			map,
			ring,
			selectLayers.value.filter((layer) => layer.kind === kind),
		).get(kind) ?? [];
	emit("area", { kind, ids, ring });
}

/** Picking and point-dropping want a crosshair, because the pointer is naming a
 * target rather than reading the collection. The area tools own the cursor while
 * they are armed — terra-draw sets it and gives it back on disarm — so this only
 * speaks when no draw is in force, and the map may arrive after a mode was set,
 * which is why it also runs on load. */
function applyCursor() {
	const map = mapRef.value;
	if (!map) return;
	if (props.pickMode === "pick" || props.pickMode === "point") {
		map.getCanvas().style.cursor = "crosshair";
	} else if (props.pickMode === null && props.areaMode === null) {
		map.getCanvas().style.cursor = "";
	}
}

function onMapLoad(event: MglEvent<"load">) {
	mapRef.value = event.map;
	event.map.on("click", onMapClick);
	// Every real click begins with a press, so the first press after a draw is
	// where selection answers again (see `swallowClosingClick`).
	event.map.on("mousedown", () => {
		swallowClosingClick = false;
	});
	applyCursor();
	fitSelection();
}

watch([() => props.pickMode, () => props.areaMode], applyCursor);

watch([() => props.selection, () => props.selectedWorkOrderCells], () => {
	void nextTick(fitSelection);
});
</script>
